import { Box, Typography } from '@mui/material';
import { EmployeeBulkEditDataRequest } from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { useActions } from '../../../../hooks/useActions';
import { useStore } from '../../../../hooks/useStore';
import { Form, FormSelect, FormTextField, useForm } from '../../../../lib/components';
import { KeyofNamespace } from '../../../../translations/keyofNs';
import { useStepper } from '../../partials/actionStepperPartial';
import { EmployeeUpdateTable } from '../table';

export const UpdateEmployeesStepOne: FC = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedUsers, setBulkUpdateType, setBulkUpdateValue, setSelectedEmployees } =
    useActions();
  const { activeStep } = useStepper();
  const { companyStore, employeeStore } = useStore();
  const { formRef, handleSubmit } = useForm();

  const options: Array<keyof EmployeeBulkEditDataRequest> = [
    'location',
    'cost_center',
    'department',
    'note'
  ];
  const updateOptions = options.map((o) => {
    let text: string;
    if (o === 'location') {
      text = 'employee:OPERATING_SITE';
    } else if (o === 'note') {
      text = 'common:NOTE';
    } else {
      text = `employee:${o.toUpperCase()}`;
    }
    return { value: o, text: t(text as KeyofNamespace<'employee' | 'common'>) };
  });

  useEffect(() => {
    if (activeStep === 3) {
      handleSubmit();
      setSelectedEmployees({});
    } else if (activeStep === 2) {
      formRef.current
        ?.validateForm()
        .then((values) => {
          for (const key in values) {
            formRef.current?.setFieldError(key, values[key] as string);
            formRef.current?.setFieldTouched(key);
          }
          if (Object.keys(values).length > 0) {
            navigate(-1);
          }
        })
        .catch((err) => console.error('VALIDATION_ERROR', err));
    }
  }, [activeStep]);

  useEffect(() => {
    const fieldToUpdate = formRef.current?.values.field_to_update;
    const value = formRef.current?.values[fieldToUpdate];
    setBulkUpdateType(fieldToUpdate);
    setBulkUpdateValue(value);
  }, [formRef.current?.values]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetch = async () => {
      try {
        await companyStore.fetchLocations();
        await companyStore.fetchDepartments();
        await companyStore.fetchCostCenters();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };

    fetch();

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          display: activeStep === 1 ? 'block' : 'none',
          '& .MuiInput-root': {
            borderRadius: '16px'
          }
        }}
      >
        <Typography paragraph>
          {t('employee:SELECTED_EMPLOYEES_DESCRIPTION', { count: selectedUsers?.length })}
        </Typography>
        <Form
          initialValues={{
            location: '',
            department: '',
            field_to_update: '',
            note: '',
            cost_center: ''
          }}
          formRef={formRef}
          onSubmit={(values) => {
            const data: EmployeeBulkEditDataRequest = {};

            for (const key in values) {
              if (values[key]) {
                data[key as keyof EmployeeBulkEditDataRequest] = values[key];
              }
            }

            return employeeStore.bulkUpdateEmployees(selectedUsers as Array<string>, data);
          }}
          useCustomButton={true}
          validationSchema={{
            field_to_update: Yup.string().required(t('form:FIELD_IS_REQUIRED')),
            note: Yup.string().when('field_to_update', {
              is: 'note',
              then: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
            }),
            location: Yup.string().when('field_to_update', {
              is: 'location',
              then: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
            }),
            department: Yup.string().when('field_to_update', {
              is: 'department',
              then: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
            }),
            cost_center: Yup.string().when('field_to_update', {
              is: 'cost_center',
              then: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
            })
          }}
        >
          <FormSelect
            name="field_to_update"
            options={updateOptions}
            placeholder={t('employee:SELECT_PROPERTY')}
            xs={6}
            additionalOnChange={(value) => {
              // reset previously selected values on change
              for (const key in formRef.current?.touched) {
                if (key === 'field_to_update') continue;
                if (key !== value) {
                  formRef.current?.setFieldValue(key, formRef.current?.initialValues[key]);
                  formRef.current?.setFieldTouched(key, false);
                }
              }
            }}
          />
          <FormTextField
            name="empty"
            isHidden={(values) => values.field_to_update}
            disabled
            xs={6}
          />
          <FormTextField
            name="note"
            isHidden={(values) => values.field_to_update !== 'note'}
            placeholder={t('common:NOTE')}
            xs={6}
          />
          <FormSelect
            name="location"
            placeholder={t('company:LOCATION')}
            isHidden={(values) => values.field_to_update !== 'location'}
            options={
              companyStore.locations?.map((l) => ({
                value: l.id,
                text: l.name
              })) || []
            }
            xs={6}
          />
          <FormSelect
            name="department"
            placeholder={t('company:DEPARTMENT')}
            isHidden={(values) => values.field_to_update !== 'department'}
            options={
              companyStore.departments?.map((d) => ({
                value: d.id,
                text: d.name
              })) || []
            }
            xs={6}
          />
          <FormSelect
            name="cost_center"
            placeholder={t('company:COST_CENTER')}
            isHidden={(values) => values.field_to_update !== 'cost_center'}
            options={
              companyStore.costCenters?.map((c) => ({
                value: c.id,
                text: c.name
              })) || []
            }
            xs={6}
          />
        </Form>
      </Box>

      {activeStep !== 3 && <EmployeeUpdateTable values={formRef.current?.values} />}
    </>
  );
});
